<template>
  <div>
    <BlBackdrop
      v-if="!deleteOpened"
      title="Edit target in databank"
      label="Update"
      description="Editing the target will only modify the information within the databank. Any changes made will not affect the target already added to the client."
      :submitLoading="submitLoading"
      :disabled="isDisabledSubmit"
      maxWidth="48rem"
      @backdrop-close="onCancel"
      @backdrop-action="onSubmit"
    >
      <div v-if="modalLoading" class="grid h-128 place-items-center">
        <div class="blum-loader"></div>
      </div>
      <div v-else class="relative">
        <div
          class="flex flex-col gap-5 px-8 overflow-y-auto"
          :style="{ width: '48rem', maxHeight: 'calc(100vh - 16rem)' }"
        >
          <div class="grid items-center grid-cols-2 gap-4 py-4 target-details">
            <div class="col-span-2">
              <BlTextSubtitle1 text="Details" />
            </div>
            <BlInputText
              class="col-span-2"
              :input="nameInput"
              @changed="inputHandler"
            />
            <!--  -->
            <BlSelectBox
              label="Data collection method"
              asterisk
              v-model="typeInput"
              placeholder="Select type"
              :options="typeOptions"
              disabled
              @change="typeInput = $event"
            />
            <BlSelectBox
              v-if="target"
              label="Curriculum"
              asterisk
              search
              v-model="curriculumIdInput"
              placeholder="Select curriculum"
              :options="[
                {
                  value: target.curriculum_id,
                  label: target.curriculum_name,
                  prefixColor: target.curriculum_color
                }
              ]"
              :fetchProperty="{
                url: `/api/v1/curriculums?sort=name_asc&query=`,
                dataKey: 'curriculums',
                countKey: 'total_count',
                optionKeys: { value: 'id', label: 'name', prefixColor: 'color' }
              }"
              @change="curriculumIdInput = $event"
              :disabled="disabledUpdateCurriculum"
            />
            <!--  -->
            <BlInputText
              class="col-span-2"
              :input="descriptionInput"
              @changed="inputHandler"
            />
            <!--  -->
            <BlInputText
              v-if="typeInput.includes('Pir')"
              :input="intervalInput"
              @changed="inputHandler"
            />
            <BlInputText
              v-if="typeInput.includes('Pir')"
              :input="durationInput"
              @changed="inputHandler"
            />
            <!--  -->
            <BlInputText
              v-if="
                typeInput.includes('Percentage') || typeInput.includes('Pir')
              "
              :input="goalInput"
              @changed="inputHandler"
            />
            <BlInputTime
              v-if="typeInput.includes('Duration')"
              :input="timeInput"
              @changed="inputHandler"
            />
            <BlInputText
              v-if="typeInput.includes('Frequency')"
              :input="goalFrequencyInput"
              @changed="inputHandler"
            />
            <div
              v-if="target.type.includes('Prompting')"
              class="flex flex-col col-span-2 gap-4 pt-8"
            >
              <div class="flex flex-col gap-2">
                <BlTextSubtitle1 text="Goal and success metric" />
                <BlTextBody2
                  v-if="promptingFormatInput === 'classic'"
                  text="Mark this target as successful when the client uses a specific prompt at least a certain number of times."
                />
                <BlTextBody2
                  v-if="promptingFormatInput === 'custom'"
                  text="The target passes if the client's score meets the goal you set."
                />
              </div>
              <div
                v-if="promptingFormatInput === 'classic'"
                class="grid grid-cols-2 gap-4"
              >
                <BlSelectBox
                  :key="`success-metric-${JSON.stringify(defaultPrompts)}`"
                  label="Prompt level"
                  asterisk
                  placeholder="Select prompt"
                  v-model="successMetricInput"
                  :options="
                    defaultPrompts.map(i => ({ value: i.name, label: i.name }))
                  "
                  @change="successMetricInput = $event"
                />
                <BlInputText :input="goalPromptInput" @changed="inputHandler" />
              </div>
              <div
                v-if="promptingFormatInput === 'custom'"
                class="grid grid-cols-2 gap-4"
              >
                <BlInputText :input="goalInput" @changed="inputHandler" />
                <BlSelectBox
                  label="Success metric"
                  asterisk
                  v-model="successMetricInput"
                  placeholder="Select success metric"
                  :options="successMetricOptions"
                  @change="successMetricInput = $event"
                />
              </div>
            </div>
            <BlSelectBox
              v-else-if="!typeInput.includes('Sbt')"
              label="Success metric"
              asterisk
              v-model="successMetricInput"
              placeholder="Select success metric"
              :options="successMetricOptions"
              @change="successMetricInput = $event"
            />
            <BlInputText
              v-if="typeInput.includes('Percentage')"
              :input="trialsInput"
              @changed="inputHandler"
            />
          </div>
          <div
            class="flex flex-col gap-2 py-4 probing"
            v-if="typeInput.includes('Percentage')"
          >
            <div class="flex items-center">
              <div :style="{ transform: 'scale(1.375)' }" class="pl-2 pr-4">
                <BlInputToggle
                  :checked="probingEnableInput"
                  @changed="probingEnableInput = !probingEnableInput"
                />
              </div>
              <BlTextSubtitle1 text="Probing" />
            </div>
            <BlTextBody2
              text="Probing can be employed to assess a skill at various stages of the therapy. Activating probing gives you additional option to conduct probes during the session."
            />
            <div v-if="probingEnableInput" class="grid grid-cols-2 gap-4">
              <BlInputText :input="probingTrialInput" @changed="inputHandler" />
              <BlInputText :input="probingGoalInput" @changed="inputHandler" />
            </div>
          </div>
          <div
            v-if="!typeInput.includes('Sbt')"
            class="flex flex-col gap-2 py-4 action-recommendation"
          >
            <div class="flex items-center gap-2">
              <div class="p-1 bg-orange-300" :style="{ borderRadius: '4px' }">
                <BlIcon icon="tooltip" class="w-5 h-5" />
              </div>
              <BlTextSubtitle1 text="Action recommendations" />
            </div>
            <BlTextBody2
              text="Get recommendation after the session when client passes certain conditions."
            />
            <ActionRecommendationInput
              :navigateSuccessMetric="false"
              :totalSuccess="totalSuccessInput"
              :consecutiveSuccess="consecutiveSuccessInput"
              @input="onInputActionRecommendation($event)"
            />
          </div>
        </div>
        <div class="absolute z-10 left-8 -bottom-14">
          <BlInputButton
            type="plain-error"
            icon="trash"
            label="Delete target"
            @clicked="deleteOpened = true"
          />
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="deleteOpened"
      title="Delete the target"
      label="Delete the target"
      type="error"
      :submitLoading="submitLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onRemove"
    >
      <div class="flex flex-wrap gap-1 px-8">
        <BlTextBody2 text="You are about to delete the target" />
        <BlTextBody2 class="font-bold" :text="`${target.name}`" />
        <BlTextBody2 text="from the databank." />
        <BlTextBody2
          text="Are you sure you want to proceed? This action is irreversible."
        />
        <BlTextBody2
          text="Please note that deleting it from the databank won't affect the target already added to the user."
        />
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import draggable from "vuedraggable";

import PromptPreview from "../prompting/Preview.vue";
import PromptItem from "../prompting/Item.vue";
import ActionRecommendationInput from "../../clients/target/modal/ActionRecommendationInput.vue";

import BlBackdrop from "../../BlBackdrop.vue";
import BlInputSelect from "../../BlInputSelect.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputTime from "../../BlInputTime.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle4 from "../../BlTextSubtitle4.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "EditDatabankModal",
  props: {
    targetId: { type: Number, required: true },
    disabledUpdateCurriculum: { type: Boolean, required: false, default: false }
  },
  components: {
    draggable,
    PromptPreview,
    PromptItem,
    ActionRecommendationInput,
    //
    BlBackdrop,
    BlInputText,
    BlInputSelect,
    BlSelectBox,
    BlTextBody2,
    BlInputTime,
    BlIcon,
    BlInputToggle,
    BlTextSubtitle1,
    BlTextSubtitle4,
    BlInputButton
  },
  data() {
    return {
      modalLoading: true,
      target: null,
      //
      submitLoading: false,
      deleteOpened: false,
      // details
      nameInput: {
        label: "Title",
        placeholder: "Target title",
        model: "",
        asterisk: true
      },
      curriculumIdInput: "",
      typeInput: "",
      typeOptions: [
        { value: "Target::Percentage", label: "Percentage" },
        { value: "Target::Duration", label: "Duration" },
        { value: "Target::Pir", label: "Partial Interval Recording" },
        { value: "Target::Frequency", label: "Frequency" },
        { value: "Target::Prompting", label: "Prompting" },
        { value: "Target::Sbt", label: "Skill-Based Treatment (SBT)" }
      ],
      descriptionInput: {
        label: "Description",
        placeholder: "Type the target description",
        model: "",
        textarea: true
      },
      goalInput: {
        label: "Goal",
        placeholder: "1-100",
        model: "",
        suffix: "percent (%)",
        asterisk: true
      },
      goalFrequencyInput: {
        label: "Goal",
        placeholder: "Target goal",
        model: "5",
        suffix: "attempt(s) per session",
        asterisk: true
      },
      goalPromptInput: {
        label: "Minimum attempts per session",
        placeholder: "Type goal",
        model: "2",
        suffix: "attempt(s)",
        asterisk: true
      },
      trialsInput: {
        label: "Number of trials",
        placeholder: "Target's number of trials",
        model: "",
        asterisk: true
      },
      timeInput: {
        label: "Goal time (hours/minutes/seconds)",
        model: "",
        asterisk: true
      },
      intervalInput: {
        label: "Interval",
        placeholder: "Target interval",
        model: "5",
        suffix: "Minutes",
        disabled: true
      },
      durationInput: {
        label: "Duration",
        placeholder: "Minimum 5 minutes",
        model: "",
        suffix: "Minutes",
        asterisk: true
      },
      successMetricInput: "",
      successMetricOptions: [
        {
          value: "equal to or greater than goal",
          label: "Equal to or greater than goal"
        },
        { value: "less than goal", label: "Less than goal" }
      ],
      // prompting
      promptingFormatInput: "classic",
      defaultPrompts: [],
      // probing
      probingEnableInput: false,
      probingTrialInput: {
        label: "Minimum trials needed",
        placeholder: "Probing's minimum trials",
        model: "",
        asterisk: true,
        suffix: "trials",
        disabled: false
      },
      probingGoalInput: {
        label: "Goal",
        placeholder: "1-100",
        model: "",
        asterisk: true,
        suffix: "percent (%)",
        disabled: false
      },
      // action recommendation
      actionRecommendationId: null,
      totalSuccessChecked: false,
      totalSuccessInput: null,
      consecutiveSuccessChecked: false,
      consecutiveSuccessInput: null
    };
  },
  computed: {
    ...mapGetters([""]),
    isDisabledSubmit() {
      // Basic checks
      if (!this.nameInput.model) return true;
      if (!this.typeInput) return true;
      if (!this.curriculumIdInput) return true;
      // Type-specific checks
      if (this.typeInput.includes("Percentage")) {
        if (!this.successMetricInput) return true;
        if (!this.goalInput.model) return true;
        if (!this.trialsInput.model) return true;
        // Probing for percentage
        if (this.probingEnableInput) {
          if (!this.probingTrialInput.model) return true;
          if (!this.probingGoalInput.model) return true;
        }
      }
      if (this.typeInput.includes("Duration")) {
        if (!this.successMetricInput) return true;
        if (this.timeInput.model === "00:00:00") return true;
        if (!this.timeInput.model) return true;
      }
      if (this.typeInput.includes("Pir")) {
        if (!this.successMetricInput) return true;
        if (!this.goalInput.model) return true;
        if (!this.intervalInput.model) return true;
        if (!this.durationInput.model) return true;
      }
      if (this.typeInput.includes("Frequency")) {
        if (!this.successMetricInput) return true;
        if (!this.goalFrequencyInput.model) return true;
      }
      if (this.typeInput.includes("Prompting")) {
        if (!this.successMetricInput) return true;
        if (this.promptingFormatInput === "classic") {
          if (!this.goalPromptInput.model) return true;
        }
        if (this.promptingFormatInput === "custom") {
          if (!this.goalInput.model) return true;
        }
      }
      // Action recommendation checks
      if (this.totalSuccessChecked) {
        if (this.totalSuccessInput === "") return true;
      }
      if (this.consecutiveSuccessChecked) {
        if (this.consecutiveSuccessInput === "") return true;
      }
      return false;
    }
  },
  watch: {},
  async mounted() {
    this.modalLoading = true;
    const params = { kind: "center", id: this.targetId };
    const { data } = await this.getTarget(params);
    this.target = data;
    this.onAssignInputs(data);
    this.modalLoading = false;
  },
  methods: {
    ...mapActions([
      "getTarget",
      "updateTarget",
      "updateActionRecommendation",
      "deleteTarget",
      "showToast"
    ]),
    onAssignInputs(target = null) {
      if (!target) return;
      // base
      this.nameInput.model = target.name;
      this.typeInput = target.type;
      this.curriculumIdInput = target.curriculum_id;
      this.descriptionInput.model = target.description;

      // percentage
      if (target.type.includes("Percentage")) {
        this.successMetricInput = target.success_metric;
        this.goalInput.model = target.goal;
        this.trialsInput.model = target.number_of_trial;
        this.probingEnableInput = target.probing_enable;
        this.probingTrialInput.model = target.probing_number_of_trial;
        this.probingGoalInput.model = target.probing_goal;
        this.probingTrialInput.disabled = !target.probing_enable;
        this.probingGoalInput.disabled = !target.probing_enable;
      }
      // duration
      if (target.type.includes("Duration")) {
        this.successMetricInput = target.success_metric;
        this.timeInput.model = target.goal_time;
      }
      // pir
      if (target.type.includes("Pir")) {
        this.successMetricInput = target.success_metric;
        this.goalInput.model = target.goal;
        this.durationInput.model = target.duration;
        this.intervalInput.model = target.interval;
      }
      // frequency
      if (target.type.includes("Frequency")) {
        this.successMetricInput = target.success_metric;
        this.goalFrequencyInput.model = target.goal;
      }
      // prompting
      if (target.type.includes("Prompting")) {
        this.successMetricInput = target.success_metric;
        this.promptingFormatInput = target.prompting_format;
        if (target.prompting_format === "classic") {
          this.goalPromptInput.model = target.goal;
        }
        if (target.prompting_format === "custom") {
          this.goalInput.model = target.goal;
        }
        this.defaultPrompts = target.prompts;
      }

      // action recommendation
      const ars = target.action_recommendations;
      if (ars && ars.at(0)) {
        const firstArs = ars.at(0);
        this.actionRecommendationId = firstArs.id;
        if (firstArs.total_success !== null) {
          this.totalSuccessChecked = true;
          this.totalSuccessInput = firstArs.total_success;
        }
        if (firstArs.consecutive_success !== null) {
          this.consecutiveSuccessChecked = true;
          this.consecutiveSuccessInput = firstArs.consecutive_success;
        }
      }
    },
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    isUsedToSuccessMetric(promptName) {
      return promptName === this.successMetric;
    },
    onInputActionRecommendation(val) {
      this.totalSuccessChecked = val.total_success_checked;
      this.totalSuccessInput = val.total_success;
      this.consecutiveSuccessChecked = val.consecutive_success_checked;
      this.consecutiveSuccessInput = val.consecutive_success;
    },
    async onSubmit() {
      const params = {
        kind: "center",
        id: this.targetId,
        target: {
          name: this.nameInput.model,
          type: this.typeInput,
          curriculum_id: this.curriculumIdInput,
          description: this.descriptionInput.model
        }
      };

      if (params.target.type === "Target::Percentage") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal = this.goalInput.model;
        params.target.number_of_trial = this.trialsInput.model;
        params.target.probing_enable = this.probingEnableInput;
        if (this.probingEnableInput) {
          params.target.probing_number_of_trial = this.probingTrialInput.model;
          params.target.probing_goal = this.probingGoalInput.model;
        }
        if (params.target.goal > 100) {
          const title = "Invalid input";
          const message = "Goal must be less than or equal to 100 percent(%).";
          this.showToast({ title, message, type: "error" });
          return;
        }
        if (params.target.probing_enable && params.target.probing_goal > 100) {
          const title = "Invalid input";
          const message =
            "Probing goal must be less than or equal to 100 percent(%).";
          this.showToast({ title, message, type: "error" });
          return;
        }
      }

      if (this.typeInput === "Target::Duration") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal_time = this.timeInput.model;
      }

      if (params.target.type === "Target::Pir") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal = this.goalInput.model;
        params.target.interval = this.intervalInput.model;
        params.target.duration = this.durationInput.model;
        if (params.target.goal > 100) {
          const title = "Invalid input";
          const message = "Goal must be less than or equal to 100 percent(%).";
          this.showToast({ title, message, type: "error" });
          return;
        }
        if (params.target.duration < 5) {
          const title = "Invalid input";
          const message = "Duration must be more than or equal to 5 minutes.";
          this.showToast({ title, message, type: "error" });
          return;
        }
      }

      if (this.typeInput === "Target::Frequency") {
        params.target.success_metric = this.successMetricInput;
        params.target.goal = this.goalFrequencyInput.model;
      }

      if (params.target.type === "Target::Prompting") {
        params.target.success_metric = this.successMetricInput;
        params.target.prompting_format = this.promptingFormatInput;
        if (this.promptingFormatInput === "classic") {
          params.target.goal = this.goalPromptInput.model;
        }
        if (this.promptingFormatInput === "custom") {
          params.target.goal = this.goalInput.model;
        }
      }

      if (this.totalSuccessChecked && this.totalSuccessInput < 1) {
        const title = "Invalid input";
        const message =
          "Please enter a value greater than or equal to 1 for total success.";
        this.showToast({ title, message, type: "error" });
        return;
      }
      if (this.consecutiveSuccessChecked && this.consecutiveSuccessInput < 2) {
        const title = "Invalid input";
        const message =
          "Please enter a value greater than or equal to 2 for consecutive.";
        this.showToast({ title, message, type: "error" });
        return;
      }

      this.submitLoading = true;
      const { success } = await this.updateTarget(params);
      if (!success) {
        this.submitLoading = false;
        return;
      }

      if (params.target.type === "Target::Sbt") {
        const title = "Target updated";
        const message = `Success! The <b>${this.nameInput.model}</b> target has been updated.`;
        this.showToast({ title, message, type: "success" });
        this.$emit("close-modal");
      }
      if (params.target.type !== "Target::Sbt") {
        this.onSubmitActionRecommendation();
      }
    },
    async onSubmitActionRecommendation() {
      const params = {
        kind: "center",
        target_id: this.targetId,
        id: this.actionRecommendationId,
        action_recommendation: {
          total_success: this.totalSuccessChecked
            ? this.totalSuccessInput
            : null,
          consecutive_success: this.consecutiveSuccessChecked
            ? this.consecutiveSuccessInput
            : null,
          recommended_action: "mastered"
        }
      };

      this.submitLoading = true;
      const { success } = await this.updateActionRecommendation(params);
      this.submitLoading = false;
      if (!success) return;

      const title = "Target updated";
      const message = `Success! The <b>${this.nameInput.model}</b> target has been updated.`;
      this.showToast({ title, message, type: "success" });
      this.$emit("close-modal");
    },
    onCancel() {
      this.$emit("close-modal");
    },
    async onRemove() {
      const data = {
        kind: "center",
        id: this.targetId,
        name: this.target.name
      };
      this.submitLoading = true;
      const { success } = await this.deleteTarget(data);
      this.submitLoading = true;
      if (!success) return;
      this.deleteOpened = false;
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/repository/databank/EditDatabankModal.vue